import GraphXAxisLabelItem from 'components/atoms/GraphXaxisLabelItem';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import {
    barWidth,
    legendData,
    salesGraphWidth,
} from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { height } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { CustomSelect } from '../DimSection';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { FilterContextData } from 'contexts/FilterContext';
import { pricingAnalyticsUrls } from 'config/urls';
import {
    categoryMovementByLevelChartParser,
    categoryMovementChartParser,
} from 'parser/pricingAnalytics/categoryMovementParser';
import { CategoryMovementOutputChartItem, PricingReport } from 'types/pricingSimulator';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { changeAnalyticsChartParser } from 'parser/pricingAnalytics/changeAnalyticsParser';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import { ConfigKeys } from 'utils/Enums';
const containerId = 'category-movement-chart-container';
const CategoryMovementContainer = () => {
    const { appConfig } = useContext(UserContext);
    const {
        selectedReportInfo,
        updateReportProperty,
        isUpdate,
        filters,
        loggedInUser,
        setSelectedReportInfo,
        showPercentageChange,
    } = useContext(PricingAnalyticsDataContext);
    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );

    const currency = fetchCurrency(appConfig);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const { dim1DropdownList = [], dim2DropdownList = [] } = dimensionConfigData ?? {};

    const { categoryMovementKpis, analyticsChartLimit } = pricingSimulatorConfig;
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const containerWidth = useContainerWidth({
        containerId: containerId,
        buffer: yAxisGraphWidth + 34 * 2 + 112,
    }); //subtracting graph width and padding on 2 sides

    const [selectedChartItems, setSelectedChartItems] = useState([null, null]);
    const [commonVariablesTopChart, setCommonVariablesTopChart] = useState<any>();
    const [commonVariablesBottomChart, setCommonVariablesBottomChart] = useState<any[] | undefined>();
    //inf scroller width ref
    const infScrollerWidthRef = useRef<any[]>([0, 0]);

    //level dropdown list
    const listToDisplay = useMemo(() => {
        return [
            { label: 'Product', options: dim1DropdownList },
            { label: 'Geo', options: dim2DropdownList },
        ];
    }, [dim1DropdownList, dim2DropdownList]);

    //set variables for top charts
    useEffect(() => {
        const topChartVariables = {
            kpi_list: categoryMovementKpis?.map((item: any) => item?.value),
            // user_name: loggedInUser,
            base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
            filters: filters,
            show_percent: showPercentageChange,
        };

        setCommonVariablesTopChart(topChartVariables);
    }, [
        selectedReportInfo?.baseScenario?.scenarioId,
        filters,
        selectedReportInfo?.categoryMovement?.input,
        showPercentageChange,
    ]);

    //set variables for bottom charts
    useEffect(() => {
        //reset inf scroller width
        infScrollerWidthRef.current = [0, 0];

        const bottomChartVariables = selectedReportInfo?.categoryMovement?.input?.map((graphItem, idx) => {
            return {
                impact_kpi_name: graphItem?.bottomChartKpiId,
                user_name: loggedInUser,
                filters: filters,
                impact_level: graphItem?.bottomChartAttributeType?.value,
                is_update: isUpdate,
                base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
                report_id: selectedReportInfo?.reportId,
                feature_name: selectedChartItems?.[idx],
                limit: analyticsChartLimit,
                offset: 0,
                show_percent: showPercentageChange,
            };
        });
        setCommonVariablesBottomChart(bottomChartVariables);
    }, [
        loggedInUser,
        filters,
        selectedChartItems,
        selectedReportInfo?.baseScenario?.scenarioId,
        selectedReportInfo?.categoryMovement?.input,
        showPercentageChange,
    ]);

    //defaulting chart selection to 1st chart item
    useEffect(() => {
        if (selectedReportInfo?.categoryMovement?.output?.topChartItems && selectedChartItems?.includes(null)) {
            const tempSelectedItemsArray: any[] = [];
            selectedReportInfo?.categoryMovement?.output?.topChartItems?.forEach((item) => {
                tempSelectedItemsArray.push(item?.data?.[0]?.featureName ?? null);
            });
            setSelectedChartItems(tempSelectedItemsArray);
        }
    }, [selectedReportInfo?.categoryMovement?.output?.topChartItems]);

    //top charts
    const { loading: topChartsLoading, error: topChartsError } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariablesTopChart?.kpi_list?.length > 0) },
        url: pricingAnalyticsUrls.getCategoryMovementKpiChartDataUrl,
        queryKey: ['pa-category-movement-chart', 'pa-category-movement-chart-top', commonVariablesTopChart],
        variables: { ...(commonVariablesTopChart || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.length) {
                throw 'No Data';
            }
            const outputObj: CategoryMovementOutputChartItem[] = categoryMovementChartParser(categoryMovementKpis, res);

            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    categoryMovement: {
                        input: prevState?.categoryMovement?.input,
                        output: {
                            bottomChartItems: prevState?.categoryMovement?.output?.bottomChartItems,
                            topChartItems: outputObj,
                        },
                    },
                };
            });
            return {};
        },
    });

    //bottom chart 1
    const { loading: bottomChart1Loading, error: bottomChart1Error } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariablesBottomChart?.[0]?.feature_name) },
        url: pricingAnalyticsUrls.getCategoryMovementChartByLevelDataUrl,
        queryKey: [
            'pa-category-movement-chart',
            'pa-category-movement-chart-bottom-1',
            commonVariablesBottomChart?.[0],
        ],
        variables: { ...(commonVariablesBottomChart?.[0] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = categoryMovementByLevelChartParser(
                categoryMovementKpis?.[0],
                res?.total_count,
                res?.data,
            );
            //if inf scroller , then merge data
            if (commonVariablesBottomChart?.[0]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[0]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[0]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[0]?.data ?? []),
                    ...outputObj?.data,
                ];
            }
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    categoryMovement: {
                        input: prevState?.categoryMovement?.input,
                        output: {
                            topChartItems: prevState?.categoryMovement?.output?.topChartItems,
                            bottomChartItems: prevState?.categoryMovement?.output?.bottomChartItems?.[1]
                                ? [outputObj, prevState?.categoryMovement?.output?.bottomChartItems?.[1]]
                                : [outputObj, { data: [] }],
                        },
                    },
                };
            });
            return {};
        },
    });

    //bottom chart 2
    const { loading: bottomChart2Loading, error: bottomChart2Error } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariablesBottomChart?.[1]?.feature_name) },
        url: pricingAnalyticsUrls.getCategoryMovementChartByLevelDataUrl,
        queryKey: [
            'pa-category-movement-chart',
            'pa-category-movement-chart-bottom-1',
            commonVariablesBottomChart?.[1],
        ],
        variables: { ...(commonVariablesBottomChart?.[1] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = categoryMovementByLevelChartParser(
                categoryMovementKpis?.[1],
                res?.total_count,
                res?.data,
            );
            //if inf scroller , then merge data
            if (commonVariablesBottomChart?.[1]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[1]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[1]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.[1]?.data ?? []),
                    ...outputObj?.data,
                ];
            }
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    categoryMovement: {
                        input: prevState?.categoryMovement?.input,
                        output: {
                            topChartItems: prevState?.categoryMovement?.output?.topChartItems,
                            bottomChartItems: prevState?.categoryMovement?.output?.bottomChartItems?.[0]
                                ? [prevState?.categoryMovement?.output?.bottomChartItems?.[0], outputObj]
                                : [{ data: [] }, outputObj],
                        },
                    },
                };
            });
            return {};
        },
    });

    const indexLoaderErrorMapping: any = {
        0: [bottomChart1Loading, bottomChart1Error],
        1: [bottomChart2Loading, bottomChart2Error],
    };

    const infiniteScrollOnNext = useCallback(
        (width: number, loading: boolean, index: number) => {
            if (!loading) {
                const newOffset = Number(commonVariablesBottomChart?.[index]?.offset) + analyticsChartLimit;

                //updating width
                infScrollerWidthRef.current.splice(index, 1, width);

                //updating offset
                setCommonVariablesBottomChart((prevValue: any) => {
                    return prevValue?.map((variable: any, idx: number) => {
                        return {
                            ...variable,
                            offset: index === idx ? newOffset : variable?.offset,
                        };
                    });
                });
            }
        },
        [commonVariablesBottomChart, setCommonVariablesBottomChart],
    );

    return (
        <div id={containerId} className="w-full mt-4 simulator-analytics-chart-container">
            <div className="mb-4 grid grid-cols-2 gap-4">
                {/* UPPER CHART CONTAINER */}
                {selectedReportInfo?.categoryMovement?.output?.topChartItems?.map((chartItem, idx) => {
                    const graphViewContainerWidth = Math.max(
                        chartItem?.data ? chartItem.data?.length * salesGraphWidth : 0,
                        containerWidth / 2,
                    );
                    const perGraphComponentWidth = graphViewContainerWidth / chartItem?.data?.length || 0;
                    return (
                        <div className="p-4 border border-backgroundGreyAlt1 rounded-lg">
                            {topChartsLoading ? (
                                <div style={{ height }}>
                                    <Loader />
                                </div>
                            ) : topChartsError ? (
                                <div style={{ height }}>
                                    <ErrorBox msg={String(topChartsError)} />
                                </div>
                            ) : (
                                <>
                                    <p className="text-subHeaderColor font-sm font-medium">
                                        {chartItem?.labelObject?.mainLabel}
                                    </p>
                                    {/* LEGEND WRAPPER */}
                                    <div className="flex justify-between my-4">
                                        <p className="font-xs w-20 text-lightBlack">{chartItem?.labelObject?.yAxis}</p>
                                        <Legend data={legendData} />
                                        <p className="w-3"></p>
                                    </div>
                                    <div id={`category-movement-left-${chartItem.id}`} className="flex ml-1 w-full">
                                        {/* Y AXIS */}
                                        <div style={{ minWidth: yAxisGraphWidth }}>
                                            <Barchart
                                                colorKey="color"
                                                dataset={[]}
                                                height={height}
                                                loading={false}
                                                chartContainer={`left-SalesY-${chartItem.id}`}
                                                fields={['category', 'sales']}
                                                minBound={scaleMinValue(80, chartItem?.minVal)}
                                                maxBound={scaleMaxValue(80, chartItem?.maxVal)}
                                                width={yAxisGraphWidth}
                                                YGrid={false}
                                                padding={[0, 0, 0, 0]}
                                                yAxisLabelFormatter={(val: any) => {
                                                    return formatNumWithKMBAlt(
                                                        val,
                                                        showPercentageChange
                                                            ? decimalForPercentage
                                                            : decimalForNonPercentage,
                                                        showPercentageChange
                                                            ? decimalForPercentage
                                                            : decimalForNonPercentage,
                                                    );
                                                }}
                                                xAxisConfig={false}
                                            />
                                        </div>
                                        {/* MAIN AXIS */}
                                        <HorizontalInfiniteScroller
                                            id={`category-movement-left-infinite-scroll${chartItem.id}`}
                                            hasMore={chartItem?.data?.length < (chartItem.count ?? 0) || false}
                                            scrollThreshold={0.9}
                                            toScrollWidth={chartItem?.width}
                                            onNext={(width: number) => {}}
                                        >
                                            <div
                                                key={chartItem?.id}
                                                id={`category-movement-left-infinite-scroll${chartItem.id}`}
                                                className="flex pb-[45px]"
                                                style={{ maxWidth: '100%', overflowX: 'auto' }}
                                            >
                                                {chartItem &&
                                                    chartItem?.data?.length > 0 &&
                                                    chartItem?.data?.map((item, index) => {
                                                        const isSelected = Boolean(
                                                            selectedChartItems?.[idx] === item?.featureName,
                                                        );
                                                        return (
                                                            <div>
                                                                <div
                                                                    onClick={() => {
                                                                        const tempArr = [...selectedChartItems];
                                                                        tempArr.splice(idx, 1, item?.featureName),
                                                                            setSelectedChartItems(tempArr);
                                                                    }}
                                                                    className={`${
                                                                        isSelected ? 'bg-bgBackground ' : ''
                                                                    } cursor-pointer`}
                                                                    style={{ height: height }}
                                                                >
                                                                    <Barchart
                                                                        barWidth={barWidth}
                                                                        //yAxisConfig={false}
                                                                        dataset={[item]}
                                                                        height={height}
                                                                        loading={false}
                                                                        chartContainer={`left-SalesX-${index}-${chartItem.id}`}
                                                                        fields={['category', 'sales']}
                                                                        intervalLabelTooltipKey={'labelSales'}
                                                                        minBound={scaleMinValue(80, chartItem?.minVal)}
                                                                        maxBound={scaleMaxValue(80, chartItem?.maxVal)}
                                                                        width={
                                                                            perGraphComponentWidth || salesGraphWidth
                                                                        }
                                                                        tooltipFormatter={(xAxis, val) => {
                                                                            return {
                                                                                name: xAxis,
                                                                                value: `${
                                                                                    idx === 0 || showPercentageChange
                                                                                        ? ''
                                                                                        : currency
                                                                                }${formatNumWithKMBAlt(
                                                                                    val,
                                                                                    showPercentageChange
                                                                                        ? decimalForPercentage
                                                                                        : decimalForNonPercentage,
                                                                                    showPercentageChange
                                                                                        ? decimalForPercentage
                                                                                        : decimalForNonPercentage,
                                                                                )}${showPercentageChange ? '%' : ''}`,
                                                                            };
                                                                        }}
                                                                        intervalFormatter={(val) => {
                                                                            return `${
                                                                                idx === 0 || showPercentageChange
                                                                                    ? ''
                                                                                    : currency
                                                                            }${formatNumWithKMBAlt(
                                                                                val,
                                                                                showPercentageChange
                                                                                    ? decimalForPercentage
                                                                                    : decimalForNonPercentage,
                                                                                showPercentageChange
                                                                                    ? decimalForPercentage
                                                                                    : decimalForNonPercentage,
                                                                            )}${showPercentageChange ? '%' : ''}`;
                                                                        }}
                                                                        colorKey={'color'}
                                                                    />
                                                                </div>
                                                                {/* X AXIS ITEM LABEL */}
                                                                <div className="flex items-center">
                                                                    <GraphXAxisLabelItem
                                                                        width={
                                                                            perGraphComponentWidth || salesGraphWidth
                                                                        }
                                                                        item={item}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </HorizontalInfiniteScroller>
                                    </div>
                                    <p className="text-lightBlack font-xs relative top-[-40px] left-[25px] flex justify-center">
                                        {chartItem.labelObject?.xAxis}
                                    </p>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="grid grid-cols-2 gap-4">
                {/* LOWER CHART CONTAINER */}
                {selectedReportInfo?.categoryMovement?.output?.bottomChartItems?.map((chartItem, idx) => {
                    const graphViewContainerWidth = Math.max(
                        chartItem?.data ? chartItem.data?.length * salesGraphWidth : 0,
                        containerWidth / 2,
                    );
                    const perGraphComponentWidth = graphViewContainerWidth / chartItem?.data?.length || 0;
                    return (
                        <div key={chartItem?.id} className="p-4 border border-backgroundGreyAlt1 rounded-lg">
                            {indexLoaderErrorMapping?.[idx]?.[0] || topChartsLoading ? (
                                <div style={{ height }}>
                                    <Loader />
                                </div>
                            ) : indexLoaderErrorMapping?.[idx]?.[1] || topChartsError ? (
                                <div style={{ height }}>
                                    <ErrorBox msg={String(indexLoaderErrorMapping?.[idx]?.[1]) ?? topChartsError} />
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-between">
                                        <p className="text-subHeaderColor font-sm font-medium">
                                            {chartItem?.labelObject?.mainLabel}
                                        </p>
                                        <div className="flex gap-2 items-center">
                                            <label className="font-xs">Level</label>
                                            <CustomSelect
                                                className="dropdown-width-multi-select dropdown-height uniqSelPSCompetitionDropdown"
                                                placeholder="Multiple Select"
                                                showArrow
                                                showSearch={false}
                                                value={
                                                    selectedReportInfo?.categoryMovement?.input?.[idx]
                                                        ?.bottomChartAttributeType?.value
                                                }
                                                onChange={(val: any, item: any) => {
                                                    const updatedInput =
                                                        selectedReportInfo?.categoryMovement?.input?.map(
                                                            (graphItem, index) => {
                                                                return {
                                                                    ...graphItem,
                                                                    bottomChartAttributeType:
                                                                        index === idx
                                                                            ? item
                                                                            : graphItem?.bottomChartAttributeType,
                                                                };
                                                            },
                                                        );
                                                    updateReportProperty('categoryMovement', {
                                                        output: selectedReportInfo?.categoryMovement?.output,
                                                        input: updatedInput,
                                                    });
                                                }}
                                                options={listToDisplay}
                                            />
                                        </div>
                                    </div>
                                    {/* LEGEND WRAPPER */}
                                    <div className="flex justify-between my-4">
                                        <p className="font-xs w-20 text-lightBlack">{chartItem?.labelObject?.yAxis}</p>
                                        <Legend data={legendData} />
                                        <p className="w-3"></p>
                                    </div>
                                    <div id={`category-movement-right-${chartItem?.id}`} className="flex ml-1 w-full">
                                        {/* Y AXIS */}
                                        <div style={{ minWidth: yAxisGraphWidth }}>
                                            <Barchart
                                                colorKey="color"
                                                dataset={[]}
                                                height={height}
                                                loading={false}
                                                chartContainer={`right-SalesY-${chartItem?.id}`}
                                                fields={['category', 'sales']}
                                                minBound={scaleMinValue(80, chartItem?.minVal)}
                                                maxBound={scaleMaxValue(80, chartItem?.maxVal)}
                                                width={yAxisGraphWidth}
                                                YGrid={false}
                                                padding={[0, 0, 0, 0]}
                                                yAxisLabelFormatter={(val: any) => {
                                                    return formatNumWithKMB(val);
                                                }}
                                                xAxisConfig={false}
                                            />
                                        </div>
                                        {/* MAIN AXIS */}
                                        <HorizontalInfiniteScroller
                                            id={`category-movement-right-infinite-scroll${chartItem?.id}`}
                                            hasMore={chartItem?.data?.length < (chartItem?.count ?? 0) || false}
                                            scrollThreshold={0.9}
                                            toScrollWidth={infScrollerWidthRef.current?.[idx] ?? 0}
                                            onNext={(width: number) => {
                                                infiniteScrollOnNext(
                                                    width,
                                                    indexLoaderErrorMapping?.[idx]?.[0] || topChartsLoading,
                                                    idx,
                                                );
                                            }}
                                        >
                                            <div
                                                id={`category-movement-right-infinite-scroll${chartItem?.id}`}
                                                className="flex pb-[45px]"
                                                style={{ maxWidth: '100%', overflowX: 'auto' }}
                                            >
                                                <div>
                                                    <div style={{ height: height }}>
                                                        <Barchart
                                                            barWidth={barWidth}
                                                            //yAxisConfig={false}
                                                            dataset={chartItem?.data || []}
                                                            height={height}
                                                            loading={false}
                                                            chartContainer={`right-SalesX-${chartItem?.id}`}
                                                            fields={['category', 'sales']}
                                                            intervalLabelTooltipKey={'labelSales'}
                                                            minBound={scaleMinValue(80, chartItem?.minVal)}
                                                            maxBound={scaleMaxValue(80, chartItem?.maxVal)}
                                                            width={graphViewContainerWidth || salesGraphWidth}
                                                            tooltipFormatter={(xAxis, val) => {
                                                                return {
                                                                    name: xAxis,
                                                                    value: `${
                                                                        idx === 0 || showPercentageChange
                                                                            ? ''
                                                                            : currency
                                                                    }${formatNumWithKMBAlt(
                                                                        val,
                                                                        showPercentageChange
                                                                            ? decimalForPercentage
                                                                            : decimalForNonPercentage,
                                                                        showPercentageChange
                                                                            ? decimalForPercentage
                                                                            : decimalForNonPercentage,
                                                                    )}${showPercentageChange ? '%' : ''}`,
                                                                };
                                                            }}
                                                            intervalFormatter={(val) => {
                                                                return `${
                                                                    idx === 0 || showPercentageChange ? '' : currency
                                                                }${formatNumWithKMBAlt(
                                                                    val,
                                                                    showPercentageChange
                                                                        ? decimalForPercentage
                                                                        : decimalForNonPercentage,
                                                                    showPercentageChange
                                                                        ? decimalForPercentage
                                                                        : decimalForNonPercentage,
                                                                )}${showPercentageChange ? '%' : ''}`;
                                                            }}
                                                            colorKey={'color'}
                                                        />
                                                    </div>
                                                    {/* X AXIS ITEM LABEL */}
                                                    <div className="flex items-center">
                                                        {chartItem?.data?.map((item: any) => {
                                                            return (
                                                                <GraphXAxisLabelItem
                                                                    width={perGraphComponentWidth || salesGraphWidth}
                                                                    item={item}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </HorizontalInfiniteScroller>
                                    </div>
                                    <p className="text-lightBlack font-xs relative top-[-40px] left-[25px] flex justify-center">
                                        {chartItem?.labelObject?.xAxis}
                                    </p>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoryMovementContainer;
